import React from 'react';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';

const { FiCheck, FiClock, FiCalendar, FiHeart, FiAnchor, FiInfo, FiMapPin } = FiIcons;
const { FaShip, FaUmbrellaBeach, FaFish, FaCompass, FaBinoculars, FaPaw, FaPlane, FaMapMarkedAlt } = FaIcons;

const Activities = () => {
  return (
    <div className="page-transition pt-36 pb-20">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl mb-6">Activities & Surroundings</h1>
          <p className="text-lg text-brand-600">Discover Uroa and the beauty of Zanzibar.</p>
        </div>

        {/* General Activities Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-24">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-brand-100">
            <h2 className="text-xl font-serif mb-4 text-brand-800">On-site Activities</h2>
            <ul className="list-disc list-inside text-brand-600 space-y-2">
              <li>Billiards</li>
              <li>Swimming in Outdoor Pool</li>
              <li>Relaxing in Garden</li>
            </ul>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-brand-100">
            <h2 className="text-xl font-serif mb-4 text-brand-800">Popular in the Area</h2>
            <ul className="list-disc list-inside text-brand-600 space-y-2">
              <li>Hiking</li>
              <li>Cycling</li>
              <li>Beach Walks</li>
            </ul>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-brand-100">
            <h2 className="text-xl font-serif mb-4 text-brand-800">Transfers</h2>
            <p className="text-brand-600 mb-4 leading-relaxed">
              Zanzibar Airport is 34 km away. We offer a paid airport shuttle for your convenience.
            </p>
          </div>
        </div>

        {/* SECTION 1: Private Boat Trip */}
        <div className="mb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Video Column - Sticky on Desktop */}
            <div className="lg:sticky lg:top-32 order-1">
              <div className="relative rounded-xl overflow-hidden shadow-xl aspect-video lg:aspect-[4/5] lg:h-auto group">
                <video 
                  src="https://canv.wasacon.com/amani-resort-zanzibar/_assets/video/d6c74c8e48c06499fad30d12a4ded552.mp4" 
                  className="w-full h-full object-cover" 
                  autoPlay 
                  loop 
                  muted 
                  playsInline 
                />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-500"></div>
                <div className="absolute bottom-6 left-6 right-6 text-white/90 text-sm font-medium tracking-widest uppercase drop-shadow-md hidden md:block">
                  Your boat. Your moments. Your Zanzibar memory.
                </div>
              </div>
            </div>

            {/* Content Column */}
            <div className="order-2 space-y-10">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-3 py-1 bg-brand-100 text-brand-800 text-[10px] font-bold uppercase tracking-widest rounded-sm">Activities</span>
                  <span className="px-3 py-1 bg-brand-100 text-brand-800 text-[10px] font-bold uppercase tracking-widest rounded-sm">Private Experience</span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-brand-900 mb-4 leading-tight">
                  Private Boat Trip <br/><span className="text-brand-500 text-2xl md:text-3xl">for Couples & Families</span>
                </h2>
                <p className="text-xl text-brand-600 font-light italic border-l-4 border-brand-300 pl-4">
                  "Romantic Ocean Experience in Zanzibar"
                </p>
              </div>

              <div className="prose prose-stone text-brand-700 leading-relaxed">
                <p>
                  Experience Zanzibar from the water with a private boat trip designed exclusively for couples and families. 
                  This romantic activity offers privacy, flexibility, and unforgettable moments on the Indian Ocean — 
                  perfect for honeymoons, anniversaries, or simply spending quality time together.
                </p>
                <p>
                  Enjoy calm turquoise waters, breathtaking coastal views, and the freedom to move at your own pace. 
                  Whether you prefer a peaceful sunset cruise, snorkeling together above colorful coral reefs, 
                  or visiting secluded sandbanks, this experience is fully focused on you and your partner.
                </p>
              </div>

              {/* Highlights & Options Grid */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="bg-brand-50 p-6 rounded-xl border border-brand-100">
                  <h3 className="text-lg font-serif mb-4 text-brand-800 flex items-center gap-2">
                    <SafeIcon icon={FiHeart} className="text-brand-500"/> Highlights
                  </h3>
                  <ul className="space-y-3 text-sm text-brand-600">
                     <li className="flex items-start gap-2"><SafeIcon icon={FiCheck} className="mt-1 text-brand-400 shrink-0"/> Completely private boat</li>
                     <li className="flex items-start gap-2"><SafeIcon icon={FiCheck} className="mt-1 text-brand-400 shrink-0"/> Romantic & peaceful atmosphere</li>
                     <li className="flex items-start gap-2"><SafeIcon icon={FiCheck} className="mt-1 text-brand-400 shrink-0"/> Flexible timing & routes</li>
                     <li className="flex items-start gap-2"><SafeIcon icon={FiCheck} className="mt-1 text-brand-400 shrink-0"/> Uncrowded locations</li>
                  </ul>
                </div>
                <div className="bg-brand-50 p-6 rounded-xl border border-brand-100">
                   <h3 className="text-lg font-serif mb-4 text-brand-800 flex items-center gap-2">
                     <SafeIcon icon={FiAnchor} className="text-brand-500"/> Experience Options
                   </h3>
                   <ul className="space-y-3 text-sm text-brand-600">
                     <li className="flex items-start gap-2"><SafeIcon icon={FaShip} className="mt-1 text-brand-400 shrink-0"/> Sunset Boat Cruise</li>
                     <li className="flex items-start gap-2"><SafeIcon icon={FaUmbrellaBeach} className="mt-1 text-brand-400 shrink-0"/> Private Sandbank Escape</li>
                     <li className="flex items-start gap-2"><SafeIcon icon={FaFish} className="mt-1 text-brand-400 shrink-0"/> Snorkeling & Reef Cruise</li>
                     <li className="flex items-start gap-2"><SafeIcon icon={FaCompass} className="mt-1 text-brand-400 shrink-0"/> Coastal & Island Exploration</li>
                   </ul>
                </div>
              </div>

              {/* Details Grid */}
              <div className="border-t border-brand-200 pt-8">
                <div className="grid grid-cols-2 gap-y-8 gap-x-4">
                   <div>
                     <h4 className="text-xs font-bold uppercase text-brand-900 mb-2 flex items-center gap-2">
                       <SafeIcon icon={FiClock} className="text-brand-500"/> Duration
                     </h4>
                     <p className="text-sm text-brand-600">Approx. 2–6 hours<br/><span className="text-xs text-brand-400">(depends on experience)</span></p>
                   </div>
                   <div>
                     <h4 className="text-xs font-bold uppercase text-brand-900 mb-2 flex items-center gap-2">
                       <SafeIcon icon={FiCalendar} className="text-brand-500"/> Best Time
                     </h4>
                     <p className="text-sm text-brand-600">Year-round<br/><span className="text-xs text-brand-400">Rec: Jun-Oct & Dec-Feb</span></p>
                   </div>
                   <div>
                     <h4 className="text-xs font-bold uppercase text-brand-900 mb-2 flex items-center gap-2">
                       <SafeIcon icon={FiHeart} className="text-brand-500"/> Ideal For
                     </h4>
                     <p className="text-sm text-brand-600">Honeymoons, Anniversaries,<br/>Romantic Getaways, Proposals</p>
                   </div>
                    <div>
                     <h4 className="text-xs font-bold uppercase text-brand-900 mb-2 flex items-center gap-2">
                       <SafeIcon icon={FiCheck} className="text-brand-500"/> Included
                     </h4>
                     <p className="text-sm text-brand-600">Private boat, Captain & Crew,<br/>Safety gear, Snorkeling gear</p>
                   </div>
                </div>
              </div>

              {/* Good to know */}
              <div className="bg-brand-50/50 p-4 rounded-lg border border-brand-100">
                <h4 className="text-sm font-bold text-brand-800 mb-2 flex items-center gap-2">
                  <SafeIcon icon={FiInfo} /> Good to Know
                </h4>
                <p className="text-xs text-brand-600 leading-relaxed">
                  Swimming is optional. Suitable for all activity levels. Weather and sea conditions may affect the route. 
                  Special requests (romantic setup, celebrations) can be arranged in advance.
                </p>
              </div>

               {/* Booking CTA */}
               <div className="bg-brand-900 text-brand-50 p-8 rounded-xl shadow-lg relative overflow-hidden">
                  <div className="relative z-10">
                    <h3 className="text-2xl font-serif mb-3">Book Your Experience</h3>
                    <p className="text-sm text-brand-200 mb-6 leading-relaxed max-w-md">
                      This activity can be arranged directly through Amani Palms by Manolo Beach Resort. 
                      Let us organize your private boat trip for a seamless experience.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button to="/contact" variant="primary" className="bg-brand-500 border-none text-white hover:bg-brand-400">
                        Enquire Now
                      </Button>
                    </div>
                  </div>
                  {/* Decorative background element */}
                  <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-brand-800 rounded-full opacity-50 blur-3xl"></div>
               </div>
            </div>
          </div>
        </div>

        {/* SECTION 2: Safari Tour Experience - New Section */}
        <div className="mb-24 pt-12 border-t border-brand-100">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            
            {/* Content Column - Left on Desktop to alternate layout */}
            <div className="order-2 lg:order-1 space-y-10">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-3 py-1 bg-brand-100 text-brand-800 text-[10px] font-bold uppercase tracking-widest rounded-sm">Activities</span>
                  <span className="px-3 py-1 bg-brand-100 text-brand-800 text-[10px] font-bold uppercase tracking-widest rounded-sm">Guided Safari</span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-brand-900 mb-4 leading-tight">
                  Safari Tour Experience <br/><span className="text-brand-500 text-2xl md:text-3xl">From Amani Palms</span>
                </h2>
                <p className="text-xl text-brand-600 font-light italic border-l-4 border-brand-300 pl-4">
                  "From palm trees to the savannah — experience the best of Tanzania"
                </p>
              </div>

              <div className="prose prose-stone text-brand-700 leading-relaxed">
                <p>
                  A stay at Amani Palms by Manolo Beach Resort can be beautifully complemented with an unforgettable African safari experience. 
                  Tanzania is home to some of the world’s most iconic wildlife landscapes, offering close encounters with nature in its purest form.
                </p>
                <p>
                  Our safari tours take you from the calm shores of Zanzibar into the heart of Tanzania’s wilderness — where vast savannahs, 
                  dramatic landscapes, and extraordinary wildlife create memories that last a lifetime. This experience is carefully arranged 
                  in cooperation with trusted, professional safari operators and adapted to your travel schedule.
                </p>
              </div>

              {/* Highlights & Options Grid */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="bg-brand-50 p-6 rounded-xl border border-brand-100">
                  <h3 className="text-lg font-serif mb-4 text-brand-800 flex items-center gap-2">
                    <SafeIcon icon={FaPaw} className="text-brand-500"/> Wildlife Highlights
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {['Elephants', 'Lions', 'Giraffes', 'Zebras', 'Buffalo', 'Wildebeest', 'Antelopes', 'Rich Birdlife'].map(animal => (
                      <span key={animal} className="text-xs bg-white px-2 py-1 rounded border border-brand-100 text-brand-600">{animal}</span>
                    ))}
                  </div>
                  <p className="text-xs text-brand-500 mt-3 italic">
                    Every safari is unique — no two days in the wild are ever the same.
                  </p>
                </div>
                <div className="bg-brand-50 p-6 rounded-xl border border-brand-100">
                   <h3 className="text-lg font-serif mb-4 text-brand-800 flex items-center gap-2">
                     <SafeIcon icon={FaMapMarkedAlt} className="text-brand-500"/> Safari Options
                   </h3>
                   <ul className="space-y-3 text-sm text-brand-600">
                     <li className="flex items-start gap-2">
                       <SafeIcon icon={FaPlane} className="mt-1 text-brand-400 shrink-0"/> 
                       <span><b>Day Safari</b><br/><span className="text-xs">Fly-in / Boat & Flight</span></span>
                     </li>
                     <li className="flex items-start gap-2">
                       <SafeIcon icon={FaBinoculars} className="mt-1 text-brand-400 shrink-0"/> 
                       <span><b>Multi-Day Safari</b><br/><span className="text-xs">Lodges / Tented Camps</span></span>
                     </li>
                     <li className="flex items-start gap-2">
                       <SafeIcon icon={FiCheck} className="mt-1 text-brand-400 shrink-0"/> 
                       <span><b>Private Tours</b><br/><span className="text-xs">Personalized routes</span></span>
                     </li>
                   </ul>
                </div>
              </div>

              {/* Details Grid */}
              <div className="border-t border-brand-200 pt-8">
                <div className="grid grid-cols-2 gap-y-8 gap-x-4">
                   <div>
                     <h4 className="text-xs font-bold uppercase text-brand-900 mb-2 flex items-center gap-2">
                       <SafeIcon icon={FiClock} className="text-brand-500"/> Duration
                     </h4>
                     <p className="text-sm text-brand-600">Day Safaris: ~1 Day<br/>Overnight: 2+ Days</p>
                   </div>
                   <div>
                     <h4 className="text-xs font-bold uppercase text-brand-900 mb-2 flex items-center gap-2">
                       <SafeIcon icon={FiCalendar} className="text-brand-500"/> Best Time
                     </h4>
                     <p className="text-sm text-brand-600">Year-round<br/><span className="text-xs text-brand-400">Highlights vary by season</span></p>
                   </div>
                   <div>
                     <h4 className="text-xs font-bold uppercase text-brand-900 mb-2 flex items-center gap-2">
                       <SafeIcon icon={FiHeart} className="text-brand-500"/> Ideal For
                     </h4>
                     <p className="text-sm text-brand-600">Couples, Nature Lovers,<br/>Adventure Seekers</p>
                   </div>
                    <div>
                     <h4 className="text-xs font-bold uppercase text-brand-900 mb-2 flex items-center gap-2">
                       <SafeIcon icon={FiCheck} className="text-brand-500"/> Included
                     </h4>
                     <p className="text-sm text-brand-600">Transfers, Guides, Game Drives,<br/>Meals (Overnight), Fees</p>
                   </div>
                </div>
              </div>

               {/* Booking CTA */}
               <div className="bg-brand-900 text-brand-50 p-8 rounded-xl shadow-lg relative overflow-hidden">
                  <div className="relative z-10">
                    <h3 className="text-2xl font-serif mb-3">Plan Your Safari</h3>
                    <p className="text-sm text-brand-200 mb-6 leading-relaxed max-w-md">
                      Safari tours are individually arranged through Amani Palms to ensure a smooth, stress-free experience. 
                      Contact us before or during your stay.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button to="/contact" variant="primary" className="bg-brand-500 border-none text-white hover:bg-brand-400">
                        Enquire Safari
                      </Button>
                    </div>
                  </div>
                  {/* Decorative background element */}
                  <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-brand-800 rounded-full opacity-50 blur-3xl"></div>
               </div>
            </div>

            {/* Video Column - Sticky on Desktop - Right Side */}
            <div className="lg:sticky lg:top-32 order-1 lg:order-2 space-y-6">
              {/* Video 1 */}
              <div className="relative rounded-xl overflow-hidden shadow-xl aspect-video group">
                <video 
                  src="https://canv.wasacon.com/amani-resort-zanzibar/_assets/video/711e0f6c6ad13ed918c0e7350641e8bc.mp4" 
                  className="w-full h-full object-cover" 
                  autoPlay 
                  loop 
                  muted 
                  playsInline 
                />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-500"></div>
              </div>
              
              {/* Video 2 */}
              <div className="relative rounded-xl overflow-hidden shadow-xl aspect-video group">
                <video 
                  src="https://canv.wasacon.com/amani-resort-zanzibar/_assets/video/472739ea2cb8063bc35f2946005a198f.mp4" 
                  className="w-full h-full object-cover" 
                  autoPlay 
                  loop 
                  muted 
                  playsInline 
                />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-500"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white/90 text-xs font-medium tracking-widest uppercase drop-shadow-md hidden md:block text-right">
                  Your Safari Adventure
                </div>
              </div>

              {/* Caption */}
               <div className="bg-brand-50 p-4 rounded-lg border border-brand-100 text-center">
                <p className="text-xs text-brand-600 italic">
                  "Experience the raw beauty of Tanzania's wildlife."
                </p>
              </div>
            </div>

          </div>
        </div>
        {/* End Safari Section */}

        <div className="bg-brand-50 rounded-xl p-8 md:p-12 mb-16">
          <h2 className="text-3xl mb-8 text-center">Nearby Highlights</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-serif text-brand-500 mb-2">18 km</div>
              <h3 className="font-medium text-brand-900">Jozani Chwaka Bay <br/> National Park</h3>
            </div>
            <div className="text-center">
              <div className="text-4xl font-serif text-brand-500 mb-2">28 km</div>
              <h3 className="font-medium text-brand-900">Kichwele Forest Reserve</h3>
            </div>
            <div className="text-center">
              <div className="text-4xl font-serif text-brand-500 mb-2">&lt;32 km</div>
              <h3 className="font-medium text-brand-900">Peace Memorial Museum <br/> (Beit el Amani)</h3>
            </div>
          </div>
        </div>

        <div className="text-center">
          <Button to="/contact" variant="primary">Plan Your Trip</Button>
        </div>
      </div>
    </div>
  );
};

export default Activities;